import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { projectsAPI, usersAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import ScanControlSettings from '../components/ScanControlSettings';
import {
  FolderOpen,
  Users,
  Settings,
  Trash2,
  Link as LinkIcon,
  Download,
  Upload,
  UserPlus,
  Gauge,
  X
} from 'lucide-react';

const Projects = () => {
  const { currentProject, projects, refreshProjects, deleteProject } = useProject();
  const { showSuccess, showError } = useNotification();
  const [selectedProject, setSelectedProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Scan control settings
  const [scanControlProject, setScanControlProject] = useState(null);

  // User creation (admin only)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  // Get current user role from localStorage
  const userRole = localStorage.getItem('userRole');
  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (currentProject) {
      setSelectedProject(currentProject);
      loadMembers(currentProject._id);
    }
  }, [currentProject]);

  const loadMembers = async (projectId) => {
    try {
      const response = await projectsAPI.getMembers(projectId);
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Не удалось загрузить участников:', error);
    }
  };

  const handleCreateInvite = async (projectId) => {
    try {
      const response = await projectsAPI.createInvite(projectId);
      setInviteCode(response.data.invite_code);
      setShowInviteModal(true);
      showSuccess('Ссылка-приглашение успешно создана');
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось создать приглашение');
    }
  };

  const handleRemoveMember = async (projectId, userId) => {
    if (!window.confirm('Удалить этого участника?')) return;

    try {
      await projectsAPI.removeMember(projectId, userId);
      await loadMembers(projectId);
      showSuccess('Участник успешно удалён');
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось удалить участника');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот проект? Все данные будут удалены безвозвратно.')) return;

    try {
      await deleteProject(projectId);
      showSuccess('Проект успешно удалён');
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось удалить проект');
    }
  };

  const handleExportProject = async (projectId) => {
    try {
      const response = await projectsAPI.exportProject(projectId);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `project_export_${projectId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Проект успешно экспортирован');
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось экспортировать проект');
    }
  };

  const handleImportProject = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      const response = await projectsAPI.importProject(importFile);
      showSuccess(response.data.message);
      setShowImportModal(false);
      setImportFile(null);
      await refreshProjects();
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось импортировать проект');
    } finally {
      setImporting(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) return;

    try {
      setCreatingUser(true);
      await usersAPI.createUser({
        username: newUsername,
        password: newPassword
      });
      showSuccess('Пользователь успешно создан');
      setShowCreateUserModal(false);
      setNewUsername('');
      setNewPassword('');
    } catch (error) {
      showError(error.response?.data?.message || 'Не удалось создать пользователя');
    } finally {
      setCreatingUser(false);
    }
  };

  const copyInviteLink = () => {
    const inviteLink = `${window.location.origin}/invite/${inviteCode}`;
    navigator.clipboard.writeText(inviteLink);
    showSuccess('Ссылка-приглашение скопирована в буфер обмена');
  };

  return (
    <div style={{ padding: '28px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
          <FolderOpen size={24} color="white" />
        </div>
        <div className="page-header-text">
          <h1>Проекты</h1>
          <p>Управление проектами и участниками</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowImportModal(true)}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: '#3b82f6',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Upload size={16} />
          Импорт проекта
        </button>

        {isAdmin && (
          <button
            onClick={() => setShowCreateUserModal(true)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <UserPlus size={16} />
            Создать пользователя
          </button>
        )}
      </div>

      {/* Projects List */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
        {projects.map((project) => (
          <div
            key={project._id}
            className="card"
            style={{
              padding: '20px',
              border: project._id === currentProject?._id ? '2px solid #10b981' : '1px solid var(--border-color)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  marginBottom: '4px'
                }}>
                  {project.name}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  {project.description || 'Без описания'}
                </p>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Users size={14} />
                  {project.members?.length || 0} участников
                </div>
              </div>
              {project._id === currentProject?._id && (
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '500',
                  borderRadius: '4px'
                }}>
                  Текущий
                </span>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              marginTop: '16px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => handleCreateInvite(project._id)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #3b82f6',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#3b82f6',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Создать ссылку-приглашение"
              >
                <LinkIcon size={14} />
                Пригласить
              </button>

              <button
                onClick={() => handleExportProject(project._id)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #10b981',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#10b981',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Экспортировать проект"
              >
                <Download size={14} />
                Экспорт
              </button>

              <button
                onClick={() => setScanControlProject(project)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#f59e0b',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Настройки сканирования"
              >
                <Gauge size={14} />
                Настройки сканирования
              </button>

              <button
                onClick={() => handleDeleteProject(project._id)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ef4444',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#ef4444',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Удалить проект"
              >
                <Trash2 size={14} />
                Удалить
              </button>
            </div>

            {/* Members List */}
            {project._id === selectedProject?._id && members.length > 0 && (
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid var(--border-color)'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}>
                  Участники
                </h4>
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px',
                      backgroundColor: 'var(--bg-tertiary)',
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}
                  >
                    <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                      {member.username}
                      {member.user_id === project.owner_id && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          color: '#f59e0b',
                          fontWeight: '500'
                        }}>
                          (Владелец)
                        </span>
                      )}
                    </span>
                    {member.user_id !== project.owner_id && (
                      <button
                        onClick={() => handleRemoveMember(project._id, member.user_id)}
                        style={{
                          padding: '4px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer'
                        }}
                        title="Удалить участника"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--text-primary)' }}>
              Ссылка-приглашение
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Отправьте эту ссылку, чтобы пригласить пользователей в проект:
            </p>
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              wordBreak: 'break-all'
            }}>
              {`${window.location.origin}/invite/${inviteCode}`}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Закрыть
              </button>
              <button
                onClick={copyInviteLink}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Копировать ссылку
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--text-primary)' }}>
              Импорт проекта
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept=".zip"
                onChange={(e) => setImportFile(e.target.files[0])}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                disabled={importing}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: importing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: importing ? 0.5 : 1
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleImportProject}
                disabled={!importFile || importing}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: (!importFile || importing) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: (!importFile || importing) ? 0.5 : 1
                }}
              >
                {importing ? 'Импорт...' : 'Импортировать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal (Admin Only) */}
      {showCreateUserModal && isAdmin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--text-primary)' }}>
              Создать пользователя
            </h2>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Имя пользователя
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Введите имя пользователя"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Пароль
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Введите пароль"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUsername('');
                  setNewPassword('');
                }}
                disabled={creatingUser}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: creatingUser ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: creatingUser ? 0.5 : 1
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleCreateUser}
                disabled={!newUsername.trim() || !newPassword.trim() || creatingUser}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#8b5cf6',
                  color: 'white',
                  cursor: (!newUsername.trim() || !newPassword.trim() || creatingUser) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: (!newUsername.trim() || !newPassword.trim() || creatingUser) ? 0.5 : 1
                }}
              >
                {creatingUser ? 'Создание...' : 'Создать пользователя'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Control Settings Modal */}
      {scanControlProject && (
        <ScanControlSettings
          project={scanControlProject}
          onClose={() => setScanControlProject(null)}
        />
      )}
    </div>
  );
};

export default Projects;
